import React, { useReducer, useContext } from "react";
import { PeraWalletConnect } from "@perawallet/connect";
import { useNavigate } from "react-router-dom";
// import { useAuth0 } from "@auth0/auth0-react";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { SignerTransaction } from "@perawallet/connect/dist/util/model/peraWalletModels";
import reducer from "../../reducers/wallet_reducer";
import getParcisionWallet from "../../apis/prod/parcision/api_parcision";
import {
  optInPera,
  closeOutPera,
  bulkCloseOutPera,
  submitBidPera,
  optInPortfolioPera,
  updatePortfolioPera,
  closeOutPortfolioPera,
  confirmPeraWalletOwnership,
} from "~/components/Wallet/Pera";
import {
  submitOptInBidParcision,
  closeOutParcision,
  bulkCloseOutParcision,
  submitBidParcision,
  optInPortfolioParcision,
  updatePortfolioParcision,
  closeOutPortfolioParcision,
} from "~/components/Wallet/Parcision";
import ParcisionCookies from "../../utils/Cookies";
import { State, Dispatch, ParcisionWalletData } from "./constants";

import * as WalletActions from "../../actions/wallet_actions";
import { OPT_IN_BEGIN, OPT_IN_ERROR, OPT_IN_SUCCESS } from "~/actions/contracts";
import { sentryWrappedException } from "~/utils/Utils";

// We're dealing with the toast ourselves
const peraWallet = new PeraWalletConnect({ shouldShowSignTxnToast: false });

function initialWalletState(): State {
  const address = ParcisionCookies.get("address"); // "ABC...123"
  const wallet = ParcisionCookies.get("wallet"); // "PERA"
  const blockchain = ParcisionCookies.get("blockchain"); // "ALGORAND"
  const net = "TESTNET";

  return {
    peraWallet,
    address,
    blockchain,
    net,
    wallet,
    reconnectLoading: false,
    reconnectError: false,
  };
}

const WalletContext = React.createContext<
  | {
      state: State;
      dispatch: Dispatch;
      handleDisconnectWallet: (showUserMessage: boolean) => Promise<void>;
      handleConnectPeraWallet: () => Promise<void>;
      handleConnectParcisionWallet: (sub: string) => Promise<ParcisionWalletData>;
      handleSignWalletTxn: (txnGroups: SignerTransaction[]) => Promise<Uint8Array[] | string | void>;
      closeOutTxn: (cid: number, param: string, prvwager: number) => Promise<string>;
      bulkCloseOutTxn: () => Promise<string>;
      submitBidTxn: (
        cid: number,
        param: string,
        cidaddr: string,
        prvwager: number,
        estimate1: number,
        estimate2: number,
        estimate3: number,
        wager: number,
        description: string
      ) => Promise<string>;
      submitOptInBidTxn: (
        cid: number,
        param: string,
        cidaddr: string,
        prvwager: number,
        estimate1: number,
        estimate2: number,
        estimate3: number,
        wager: number,
        description: string,
        contractsDispatcher: React.Dispatch<any>
      ) => Promise<string>;

      submitPortfolioOptInTxn: (
        pid: number,
        phash: string,
        pverified: boolean,
        rhash: string,
        rverified: boolean,
        description: string
      ) => Promise<string>;

      submitPortfolioUpdateTxn: (
        pid: number,
        phash: string,
        pverified: boolean,
        rhash: string,
        rverified: boolean,
        description: string
      ) => Promise<string>;

      submitPortfolioCloseOutTxn: (pid: number) => Promise<string>;

      confirmWalletOwnership: () => Promise<string>;
    }
  | undefined
>(undefined);

export function WalletProvider({ children }: { children: React.ReactNode }) {
  const initialState = { ...initialWalletState() };

  const [state, dispatch] = useReducer(reducer, initialState);

  // const { isAuthenticated } = useAuth0();

  const navigate = useNavigate();

  /// //////////////
  // PERA WALLET //
  /// //////////////

  // DISCONNECT THEN DELETE AWS COOKIES
  const handleDisconnectWallet = async (showUserMessage: boolean) => {
    /// //////////////
    // PERA WALLET //
    /// //////////////
    let disconnectToastId;
    if (state.wallet === "PERA") {
      disconnectToastId = showUserMessage ? toast.info("Disconnecting Wallet.", { autoClose: 3000 }) : null;
      await peraWallet.disconnect();
      if (showUserMessage) {
        toast.update(disconnectToastId, {
          type: toast.TYPE.SUCCESS,
          autoClose: 3000,
          render: () => <div>Wallet Disconnected...</div>,
        });
      }
    }

    /// ///////////////////
    // PARCISION WALLET //
    /// ///////////////////

    /// /////////////////////
    // DESTORY AWS TOKENS //
    /// /////////////////////
    ParcisionCookies.remove("web3_session");
    ParcisionCookies.remove("address");
    ParcisionCookies.remove("blockchain");
    ParcisionCookies.remove("net");
    ParcisionCookies.remove("wallet");

    // UPDATE STATE
    dispatch({
      type: WalletActions.SET_ADDRESS_SUCCESS,
      payload: { address: null, wallet: null, blockchain: null, net: null },
    });

    // return to landing page
    navigate("/wallets");
  };

  // CONNECT
  const handleConnectPeraWallet = async () => {
    dispatch({ type: WalletActions.SET_ADDRESS_BEGIN });
    await peraWallet
      .connect()
      .then((newaddresses) => {
        const connectToastId = toast.info("Connecting Pera wallet.", {
          autoClose: 3000,
        });
        peraWallet.connector.on("disconnect", (error, payload) => {
          const toastId = toast.info("Disconnecting Pera wallet.", {
            autoClose: 3000,
          });
          if (error) {
            Sentry.setContext("Payload", payload);
            sentryWrappedException(error);
            toast.update(toastId, {
              render: () => <div>Pera wallet connection error.</div>,
            });
          }

          handleDisconnectWallet(true).then(
            () =>
              toast.update(connectToastId, {
                type: toast.TYPE.SUCCESS,
                autoClose: 3000,
                render: () => <div>Success!</div>,
              }),
            (reason: string) => {
              sentryWrappedException(new Error(`Failure reason: ${reason}`));
              toast.update(connectToastId, {
                autoClose: 2000,
                render: () => <div>Pera wallet connection error.</div>,
              });
            }
          );
          return null;
        });

        ParcisionCookies.set("wallet", "PERA");
        dispatch({
          type: WalletActions.SET_ADDRESS_SUCCESS,
          payload: {
            address: newaddresses[0],
            wallet: "PERA",
            blockchain: "ALGORAND",
            net: "TESTNET",
          },
        });
      })
      .catch((error) => {
        sentryWrappedException(error);
        toast.warn("Wallet connect modal closed.");
        if (error?.data?.type !== "CONNECT_MODAL_CLOSED") {
          // NOTIFY USER OF AN ERROR & TO TRY AGAIN
          dispatch({ type: WalletActions.SET_ADDRESS_ERROR });
          handleDisconnectWallet(true);
        }
      });
  };

  // TRY TO RECONNECT
  const checkPera = () => {
    /// //////////////
    // PERA WALLET //
    /// //////////////

    // handleDisconnectWallet(false);

    if (ParcisionCookies.check("wallet")) {
      if (ParcisionCookies.get("wallet") === "PERA") {
        // toast.info("Reconnectng Pera wallet.", {
        //   autoClose: 3000,
        // });
        // Reconnect to the session when the component is mounted
        peraWallet
          .reconnectSession()
          .then((addresses) => {
            // Setup the disconnect event listener
            peraWallet.connector?.on("disconnect", () => {
              const doHandleDisconnectWallet = async () => {
                await handleDisconnectWallet(false);
              };

              doHandleDisconnectWallet().catch(sentryWrappedException);
              return null;
            });

            dispatch({ type: WalletActions.RECONNECT_BEGIN });
            if (peraWallet.isConnected && addresses.length) {
              ParcisionCookies.set("wallet", "PERA");
              dispatch({
                type: WalletActions.RECONNECT_SUCCESS,
                payload: {
                  address: addresses[0],
                  wallet: "PERA",
                  blockchain: "ALGORAND",
                  net: "TESTNET",
                },
              });
            }
          })
          .catch((e) => {
            sentryWrappedException(e);
            toast.error("Pera reconnect error.", { autoClose: false });
            dispatch({ type: WalletActions.RECONNECT_ERROR });
            handleDisconnectWallet(true).catch(sentryWrappedException);
            throw e;
          });
      }

      if (!state.reconnectLoading && !state.reconnectError) {
        /// ///////////////////
        // PARCISION WALLET //
        /// ///////////////////
        /// /////////////////////
        // AWS REFRESH TOKENS //
        /// /////////////////////
        /*
        const checkAWSToken = async () => {
          if (ParcisionCookies.check("web3_expireTime") && ParcisionCookies.check("web3_refreshToken")) {
            if (new Date(ParcisionCookies.get("web3_expireTime")) <= new Date()) {
              const refreshed = await TokenManager(state.address, state.blockchain);
              if (!refreshed) {
                await handleDisconnectWallet();
              }
            }
          }
        };
        if (state.address) {
          checkAWSToken().catch(console.error);
        }
        */
      }
    }
  };

  /// ///////////////////
  // PARCISION WALLET //
  /// ///////////////////

  // CONNECT
  const handleConnectParcisionWallet = async (sub: string) => {
    dispatch({ type: WalletActions.SET_ADDRESS_BEGIN });
    const toastId = toast.info("Connecting Parcision wallet.");
    try {
      // const parcision_wallet = "ABC123" // BACKEND USING AUTH0 CREDENTIALS
      const parcision_wallet = await getParcisionWallet(sub); // get Parcision wallet & get AWS tokens
      ParcisionCookies.set("wallet", "PARCISION");
      dispatch({
        type: WalletActions.SET_ADDRESS_SUCCESS,
        payload: {
          address: parcision_wallet.address,
          wallet: "PARCISION",
          blockchain: "ALGORAND",
          net: "TESTNET",
        },
      });
      toast.update(toastId, {
        type: toast.TYPE.SUCCESS,
        autoClose: 3000,
        render: () => <div>Parcision wallet connected.</div>,
      });
      return parcision_wallet;
    } catch (error) {
      sentryWrappedException(error);
      dispatch({ type: WalletActions.SET_ADDRESS_ERROR });
      toast.update(toastId, {
        type: toast.TYPE.ERROR,
        autoClose: 3000,
        render: () => <div>Failed to connect to Parcision wallet.</div>,
      });
      // NOTIFY USER OF AN ERROR & TO TRY AGAIN
      throw error;
    }
  };

  /// /////////////////
  // WALLET ACTIONS //
  /// /////////////////

  // SIGN TXN
  const handleSignWalletTxn = async (txnGroups: SignerTransaction[]) => {
    dispatch({ type: WalletActions.SIGN_TXN_BEGIN });

    /// //////////////
    // PERA WALLET //
    /// //////////////
    if (state.wallet === "PERA") {
      checkPera(); // IF PERA IS CONNECTED

      try {
        const signedTxn = await peraWallet.signTransaction([txnGroups]);
        dispatch({ type: WalletActions.SIGN_TXN_SUCCESS });
        return signedTxn;
      } catch (error) {
        sentryWrappedException(error);
        toast.error(error.message);
        dispatch({ type: WalletActions.SIGN_TXN_ERROR });
        // NOTIFY USER OF AN ERROR & TO TRY AGAIN
        throw error;
      }
    }

    /// ///////////////////
    // PARCISION WALLET //
    /// ///////////////////
    if (state.wallet === "PARCISION") {
      // do we need to do something here?
    }
    return null;
  };

  // OPT IN TXN
  const submitOptInBidTxn = async (
    cid: number,
    gparam: string,
    cidaddr: string,
    prvwager: number,
    estimate1: number,
    estimate2: number,
    estimate3: number,
    wager: number,
    description: string,
    contractsDispatcher: React.Dispatch<any>
  ) => {
    contractsDispatcher({ type: OPT_IN_BEGIN, payload: { cid } });
    let optInResults = null;
    if (state.wallet === "PERA") {
      checkPera(); // IF PERA IS CONNECTED

      optInResults = await optInPera(
        cid,
        gparam,
        cidaddr,
        prvwager,
        estimate1,
        estimate2,
        estimate3,
        wager,
        description,
        handleSignWalletTxn
      );
    }
    if (state.wallet === "PARCISION") {
      optInResults = await submitOptInBidParcision(cid, gparam, estimate1, estimate2, estimate3, wager, description);
    }
    // TODO: pera wallet returns 0 if the wallet app user cancels the transaction.
    // Are there situations in `optInParcision` where an error won't be thrown,
    // but the value should be considered an error?
    if (optInResults === "Low Funds") {
      contractsDispatcher({ type: OPT_IN_ERROR, payload: { cid } });
      return "Low Funds";
    }
    if (optInResults === 0) {
      contractsDispatcher({ type: OPT_IN_ERROR, payload: { cid } });
      return "";
    }
    contractsDispatcher({ type: OPT_IN_SUCCESS, payload: { cid, optInResults } });
    return "";
  };

  // CLOSE OUT TXN
  const closeOutTxn = async (cid: number, param: string, prvwager: number) => {
    let closeOutResults = null;
    if (state.wallet === "PERA") {
      checkPera(); // IF PERA IS CONNECTED

      closeOutResults = await closeOutPera(cid, param, prvwager, handleSignWalletTxn);
    }
    if (state.wallet === "PARCISION") {
      closeOutResults = await closeOutParcision(cid, param);
    }
    if (closeOutResults === "Low Funds") {
      return "Low Funds";
    }
    return "";
  };

  // BULK CLOSE OUT TXN
  const bulkCloseOutTxn = async () => {
    let bulkCloseOutResults = null;

    if (state.wallet === "PERA") {
      checkPera(); // IF PERA IS CONNECTED

      bulkCloseOutResults = await bulkCloseOutPera(handleSignWalletTxn);
    }
    if (state.wallet === "PARCISION") {
      bulkCloseOutResults = await bulkCloseOutParcision();
    }
    if (bulkCloseOutResults === "Low Funds") {
      return "Low Funds";
    }
    return "";
  };

  // SUBMIT BID TXN
  const submitBidTxn = async (
    cid: number,
    gparam: string,
    cidaddr: string,
    prvwager: number,
    estimate1: number,
    estimate2: number,
    estimate3: number,
    wager: number,
    description: string
  ) => {
    let data;
    if (state.wallet === "PERA") {
      checkPera(); // IF PERA IS CONNECTED

      data = await submitBidPera(
        cid,
        gparam,
        cidaddr,
        handleSignWalletTxn,
        prvwager,
        estimate1,
        estimate2,
        estimate3,
        wager,
        description
      );
    }
    if (state.wallet === "PARCISION") {
      data = await submitBidParcision(cid, gparam, estimate1, estimate2, estimate3, wager, description);
    }
    if (data === "Low Funds") {
      return "Low Funds";
    }
    return data;
  };

  /// ////////////
  // PORTFOLIO //
  /// ////////////

  // OPT IN TXN
  const submitPortfolioOptInTxn = async (
    pid: number,
    phash: string,
    pverified: boolean,
    rhash: string,
    rverified: boolean,
    description: string
  ) => {
    let optInResults = null;

    if (state.wallet === "PERA") {
      checkPera(); // IF PERA IS CONNECTED

      optInResults = await optInPortfolioPera(
        pid,
        phash,
        pverified,
        rhash,
        rverified,
        description,
        handleSignWalletTxn
      );
    } else if (state.wallet === "PARCISION") {
      optInResults = await optInPortfolioParcision(pid, phash, pverified, rhash, rverified, description);
    }
    if (optInResults === "Low Funds") {
      return "Low Funds";
    }
    return optInResults;
  };

  // CLOSE OUT TXN
  const submitPortfolioCloseOutTxn = async (pid: number) => {
    let closeOutResults = null;

    if (state.wallet === "PERA") {
      checkPera(); // IF PERA IS CONNECTED

      closeOutResults = await closeOutPortfolioPera(pid, handleSignWalletTxn);
    } else if (state.wallet === "PARCISION") {
      closeOutResults = await closeOutPortfolioParcision(pid);
    }
    if (closeOutResults === "Low Funds") {
      return "Low Funds";
    }
    return "";
  };

  // SUBMIT BID TXN
  const submitPortfolioUpdateTxn = async (
    cid: number,
    phash: string,
    pverified: boolean,
    rhash: string,
    rverified: boolean,
    description: string
  ) => {
    let data;

    if (state.wallet === "PERA") {
      checkPera(); // IF PERA IS CONNECTED

      data = await updatePortfolioPera(cid, phash, pverified, rhash, rverified, description, handleSignWalletTxn);
    } else if (state.wallet === "PARCISION") {
      data = await updatePortfolioParcision(cid, phash, pverified, rhash, rverified, description);
    }
    if (data === "Low Funds") {
      return "Low Funds";
    }
    return data;
  };

  // CLOSE OUT TXN
  const confirmWalletOwnership = async () => {
    let message = "";

    if (state.wallet === "PERA") {
      checkPera(); // IF PERA IS CONNECTED

      message = await confirmPeraWalletOwnership();
    }

    if (message === "Verify Pera Wallet") {
      return "Verify Pera Wallet";
    }
    return "";
  };

  const providerValue = {
    state,
    dispatch,
    handleDisconnectWallet,
    handleConnectPeraWallet,
    handleConnectParcisionWallet,
    handleSignWalletTxn,
    // optInBidTxn,
    closeOutTxn,
    bulkCloseOutTxn,
    submitBidTxn,
    submitOptInBidTxn,

    submitPortfolioOptInTxn,
    submitPortfolioUpdateTxn,
    submitPortfolioCloseOutTxn,

    confirmWalletOwnership,
  };
  return <WalletContext.Provider value={providerValue}>{children}</WalletContext.Provider>;
}

// make sure use
export const useWalletContext = () => {
  return useContext(WalletContext);
};
